import CameraOutlineIcon from 'assets/icons/camera_outline.svg?react';
import VideocamOffIcon from 'assets/icons/ic_videocam_off.svg?react';
import WifiOutlineIcon from 'assets/icons/wifi_outline.svg?react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, InlineNotification, Loader, Tag, TagColor, TagSize } from '@spotted-zebra-uk/ui-components';
import Icon from '@/components/atoms/Icon';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { MicrophoneCheck } from './MicrophoneCheck';
import styles from './StepDeviceSetup.module.scss';

interface StepDeviceSetupProps {
  onContinue: () => void;
  nextStepLabel: string;
}

export function StepDeviceSetup({
  onContinue,
  nextStepLabel,
}: StepDeviceSetupProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // The stream is state (not just a ref) so the preview and mic indicator
  // re-render onto the fresh stream after "Request access again" — a ref swap
  // alone leaves them analysing the old, stopped tracks.
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraGranted, setCameraGranted] = useState(false);
  const [micGranted, setMicGranted] = useState(false);
  const [micWorking, setMicWorking] = useState(false);
  const { online, isPoorConnection } = useNetworkStatus();

  // Only the latest request may install its stream. Overlapping requests
  // (mount plus a quick "Try again") otherwise left an older stream live and
  // swapped the mic meter onto whichever one resolved last.
  const latestRequestRef = useRef(0);

  const requestMediaPermissions = useCallback(async () => {
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      const nextStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      if (requestId !== latestRequestRef.current) {
        nextStream.getTracks().forEach(track => track.stop());
        return;
      }

      streamRef.current = nextStream;
      setStream(nextStream);
      setCameraGranted(true);
      setMicGranted(true);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error('Error accessing media devices:', error);
      setStream(null);
      setCameraGranted(false);
      setMicGranted(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      // Invalidate any permission request still pending, so a stream that
      // resolves after the candidate has left is stopped, not installed into
      // an unmounted component with the camera light left on.
      latestRequestRef.current += 1;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
      });
    }
  }, [stream]);

  useEffect(() => {
    requestMediaPermissions();
  }, [requestMediaPermissions]);

  const allPermissionsGranted = cameraGranted && micGranted;

  function handleContinue() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    onContinue();
  }

  return (
    <div className={styles.stepDeviceSetup}>
      <div className={styles.content}>
        <h1 className={styles.title}>{t('aiInterview.checkYourSetup')}</h1>

        {!allPermissionsGranted && (
          <InlineNotification>
            {/* Every locale wraps the button word in <1>; map it explicitly
                rather than relying on JSX child order, where a formatting
                space once became child 1 and dropped the word (CR-493). */}
            <Trans
              i18nKey="aiInterview.browserPermissionNotice"
              components={{ 1: <span className={styles.bold} /> }}
            />
          </InlineNotification>
        )}

        <div className={styles.devicesBox}>
          <MicrophoneCheck
            stream={micGranted ? stream : null}
            onWorkingChange={setMicWorking}
            onRetry={requestMediaPermissions}
          />

          <div className={styles.deviceSection}>
            <div className={styles.deviceHeader}>
              <div className={styles.deviceIcon}>
                {cameraGranted ? (
                  <CameraOutlineIcon />
                ) : (
                  <VideocamOffIcon />
                )}
              </div>
              <span className={styles.deviceTitle}>{t('aiInterview.camera')}</span>
            </div>
            {cameraGranted ? (
              <>
                <p className={styles.deviceHint}>
                  {t('aiInterview.faceVisible')}
                </p>
                <div className={styles.cameraPreview}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={styles.videoFeed}
                  />
                </div>
              </>
            ) : (
              <>
                <p className={styles.deviceHint}>
                  {t('aiInterview.pleaseAllowAccess')}
                </p>
                <div className={styles.cameraPlaceholder}>
                  <Loader variant="spinner" size='small' />
                  <span className={styles.cameraPlaceholderLabel}>
                    {t('aiInterview.waitingForPermission')}
                  </span>
                </div>
              </>
            )}
          </div>

          <InternetConnectionSection
            online={online}
            isPoorConnection={isPoorConnection}
          />
        </div>
      </div>

      {allPermissionsGranted ? (
        <Button
          onClick={handleContinue}
          // The next steps need the network, so continuing offline only
          // moves the failure one screen later.
          disabled={!micWorking || !online}
          fullWidth
          rightIcon={<Icon icon="right_arrow" />}
        >
          {nextStepLabel}
        </Button>
      ) : (
        <Button
          onClick={requestMediaPermissions}
          variant="secondary"
          fullWidth
        >
          {t('aiInterview.requestAccessAgain')}
        </Button>
      )}
    </div>
  );
}

// Fed from the same hook as the top banner (ConnectionQualityProvider), so the
// tag and the banner never disagree about the connection.
function InternetConnectionSection({
  online,
  isPoorConnection,
}: {
  online: boolean;
  isPoorConnection: boolean;
}) {
  const { t } = useTranslation();

  const status = !online
    ? {
        iconClass: styles.deviceIconError,
        tagColor: TagColor.RED,
        labelKey: 'aiInterview.connectionLost',
        hintKey: 'aiInterview.pleaseReconnect',
      }
    : isPoorConnection
      ? {
          iconClass: styles.deviceIconWarning,
          tagColor: TagColor.YELLOW,
          labelKey: 'aiInterview.weak',
          hintKey: 'aiInterview.weakConnectionHint',
        }
      : {
          iconClass: '',
          tagColor: TagColor.GREEN,
          labelKey: 'aiInterview.stable',
          hintKey: null,
        };

  return (
    <div className={styles.deviceSection}>
      <div className={styles.deviceHeader}>
        <div className={`${styles.deviceIcon} ${status.iconClass}`}>
          <WifiOutlineIcon />
        </div>
        <span className={styles.deviceTitle}>{t('aiInterview.internetConnection')}</span>
        <Tag
          isPresentational
          isDismissible={false}
          tagColor={status.tagColor}
          size={TagSize.SMALL}
        >
          {t(status.labelKey)}
        </Tag>
      </div>
      {status.hintKey && (
        <p className={styles.deviceHint}>{t(status.hintKey)}</p>
      )}
    </div>
  );
}
